import { AxiosError } from 'axios';
import { useFormik } from 'formik';
import { useCallback, useEffect } from 'react';

import useTriggeredRequest from '@api/hooks/useTriggeredRequest';
import AccountClientRequests from '@api/requests';

import { showToast } from '@app/providers/ToastProvider';
import { store } from '@app/redux/store';

import { VerifyEmailFormProps, VerifyEmailFormValues } from './interfaces';
import verifyEmailFormValidationScheme from './validationScheme';

function useVerifyEmailForm({ initialValues, onSubmit }: Pick<VerifyEmailFormProps, 'initialValues' | 'onSubmit'>) {
	const { startRequest: sendVerificationEmailCode, isPending: isSendingVerificationEmailCode } = useTriggeredRequest(
		AccountClientRequests.Users.sendVerificationEmailCode,
	);

	const formik = useFormik<VerifyEmailFormValues>({
		initialValues,
		validationSchema: verifyEmailFormValidationScheme,
		onSubmit,
	});

	const handleSendVerificationEmailCode = useCallback(async () => {
		try {
			await sendVerificationEmailCode({
				branch: store.getState().branchEnvironment.variables.branch_id,
				contact: store.getState().auth.currentUser?.email || '',
				type: 'email',
			});
		} catch (e) {
			const { response } = e as AxiosError<{
				messages: string[];
			}>;

			showToast.error(response?.data?.messages?.join(' ') || 'Oops. Something went wrong.');
		}
	}, [sendVerificationEmailCode]);

	useEffect(() => {
		handleSendVerificationEmailCode();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		isSendingVerificationEmailCode,
		handleSendVerificationEmailCode,
		formik,
	};
}

export default useVerifyEmailForm;
